// What Settings says a group is doing: one line of plain facts for the open
// group, and each section's current value beside its name. Every fact comes
// from a read its section already makes; a group's reads start when it opens,
// never on the home. A read that fails or is refused leaves its fact out.
import { useEffect, useRef, useState } from "react";
import { getChatCreds } from "../../lib/chatCreds.js";
import { listDirectoryIntegrations } from "../../lib/identityClient.js";
import { ESCALATION_STATES, listEscalationRequests } from "../../lib/escalationClient.js";
import { loadDataSource } from "../../pages/dataSources/useDataSources.js";
import { SOURCES, sourceHealth } from "../../pages/dataSources/model.js";
import { TONE } from "../statusCard.jsx";

const NUM = new Intl.NumberFormat("en-US");
const plural = (n, one, many = `${one}s`) => `${NUM.format(n)} ${n === 1 ? one : many}`;
// Settled reads by name: a value, or null where the read failed.
async function settle(tasks) {
    const names = Object.keys(tasks);
    const results = await Promise.allSettled(names.map((name) => tasks[name]));
    return Object.fromEntries(names.map((name, i) => [name, results[i].status === "fulfilled" ? results[i].value : null]));
}

const CHAT_PROVIDERS = { anthropic: "Anthropic", openrouter: "OpenRouter", openai: "OpenAI", custom: "your own gateway" };

// Each group's reads, given the sections the caller can see, and what they say.
export const SUMMARIES = {
    async services(ids) {
        const keys = SOURCES.map((s) => s.key).filter((key) => ids.has(key));
        const r = await settle({ directory: listDirectoryIntegrations(), ...Object.fromEntries(keys.map((key) => [key, loadDataSource(key)])) });
        const directory = { data: r.directory, loaded: true, loading: false, error: null };
        const values = {};
        const statuses = {};
        let connected = 0;
        for (const key of keys) {
            const health = sourceHealth(key, { data: r[key], loaded: true, loading: false, error: r[key] == null ? "Could not load this source." : null }, directory);
            values[key] = health.label;
            statuses[key] = { tone: health.tone, label: health.label };
            if (health.tone === TONE.OK) connected += 1;
        }
        return { now: keys.length ? `${NUM.format(connected)} of ${plural(keys.length, "service")} ${connected === 1 ? "sends" : "send"} Origin its AI activity.` : null, values, statuses };
    },

    async access(ids) {
        if (!ids.has("requests")) return { now: null, values: {} };
        const page = await listEscalationRequests({ states: [ESCALATION_STATES.PENDING] });
        const waiting = page?.tenantWide ? (page.requests ?? []).length : 0;
        return {
            now: waiting ? `${plural(waiting, "elevation request")} ${waiting === 1 ? "is" : "are"} waiting.` : null,
            // Only a count worth acting on sits beside the page's name.
            values: { requests: page?.tenantWide && waiting ? `${NUM.format(waiting)} waiting` : undefined },
        };
    },

    async chat() {
        const creds = await getChatCreds();
        const provider = creds.managedDefault && !creds.hasApiKey ? "Origin" : CHAT_PROVIDERS[creds.provider] || "";
        const model = creds.model || (creds.managedDefault ? "Origin's default model" : "");
        return {
            now: model ? `Dashboard chat runs on ${model}${provider ? ` from ${provider}` : ""}.` : "Dashboard chat has no model yet.",
            values: {
                model: model || "Not set",
                provider: creds.hasApiKey ? `${provider}, key saved` : creds.managedDefault ? "Managed by Origin" : "No key",
            },
        };
    },
};


// A group's line and section values, read when the group opens and again
// when the open section (`open`) closes or another opens in its place.
export function useGroupSummary(group, open = "") {
    const [summary, setSummary] = useState({ id: null, now: null, values: {} });
    const id = group?.id ?? null;
    const sections = group ? group.sections.map((s) => s.id).join(",") : "";
    const [left, setLeft] = useState(0);
    const last = useRef(open);
    useEffect(() => {
        if (last.current && last.current !== open) setLeft((n) => n + 1);
        last.current = open;
    }, [open]);
    useEffect(() => {
        if (!id || !SUMMARIES[id]) return undefined;
        let cancelled = false;
        SUMMARIES[id](new Set(sections.split(",")))
            .then((next) => { if (!cancelled) setSummary({ id, ...next }); })
            .catch(() => { if (!cancelled) setSummary({ id, now: null, values: {} }); });
        return () => { cancelled = true; };
    }, [id, sections, left]);
    // `pending` until the group's first read answers, so its page can hold the
    // line's place rather than push its sections down when it lands.
    return summary.id === id ? summary : { id, now: null, values: {}, pending: Boolean(id && SUMMARIES[id]) };
}

// Access requests waiting for this reader to decide: the home's one read.
export function useWaitingRequests(enabled) {
    const [waiting, setWaiting] = useState(0);
    useEffect(() => {
        if (!enabled) return undefined;
        let cancelled = false;
        listEscalationRequests({ states: [ESCALATION_STATES.PENDING] })
            .then((page) => { if (!cancelled) setWaiting(page?.tenantWide ? (page.requests ?? []).length : 0); })
            .catch(() => { if (!cancelled) setWaiting(0); });
        return () => { cancelled = true; };
    }, [enabled]);
    return enabled ? waiting : 0;
}
